import { InputAdornment } from '@mui/material';
import { FormikHandlers, FormikState } from 'formik';
import React, { useCallback, useEffect, useMemo, useState, ReactElement } from 'react';
import { NumberFormatProps, NumberFormatValues } from 'react-number-format';

import { NumberFormatField } from '../number-format-field';

type Props = Omit<
  React.ComponentProps<typeof NumberFormatField>,
  'value' | 'onChange' | 'error' | 'helperText' | 'defaultValue'
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikHandlers & FormikState<any>;
  name: string;
};

const format: NumberFormatProps<unknown> = {
  thousandSeparator: true,
  decimalScale: 2,
};

const FormikCurrencyField = (props: Props): ReactElement => {
  const { name, formik, disabled, InputProps, ...rest } = props;
  const meta = formik.getFieldMeta<number | undefined>(name);
  const cents = meta.value;

  const [centsValue, setCentsValue] = useState(cents);
  const [inputValue, setInputValue] = useState((cents && cents / 100) ?? '');

  useEffect(() => {
    if (cents !== centsValue) {
      setInputValue((cents && cents / 100) ?? '');
    }
  }, [cents, centsValue]);

  // saving reference to handleChange to avoid explicit dependency on formik
  // because formik mutates with every value change, while handleChange is stable
  const handleChange = formik.handleChange;
  const onValueChange = useCallback(
    (values: NumberFormatValues) => {
      const value = values.floatValue;
      const centsValue = value && Math.round(value * 100);

      const e = {
        target: { name, value: centsValue ?? '' },
      };
      handleChange(e);
      setCentsValue(centsValue);
      setInputValue(values.formattedValue);
    },
    [handleChange, name],
  );

  const InputPropsWithAdornment = useMemo(
    () => ({
      ...InputProps,
      startAdornment: (
        <InputAdornment disableTypography={disabled} position="start">
          $
        </InputAdornment>
      ),
    }),
    [InputProps, disabled],
  );

  return (
    <NumberFormatField
      disabled={disabled}
      InputProps={InputPropsWithAdornment}
      {...rest}
      name={name}
      value={inputValue}
      onValueChange={onValueChange}
      error={meta.touched && !!meta.error}
      helperText={meta.touched && meta.error}
      format={format}
    />
  );
};

export { FormikCurrencyField };
